import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { api } from '../api';
import '../styles/login.css';
import { useAuth } from '../context/AuthContext';
import { POST_LOGIN_REDIRECT_KEY } from '../constants';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fromLocation = useMemo(() => {
    const state = location.state as { from?: Location } | null;
    return state?.from;
  }, [location.state]);

  const buildRedirectPath = (loc?: Partial<Location> | null) => {
    if (!loc) {
      return '/';
    }
    const pathname = loc.pathname ?? '/';
    const search = loc.search ?? '';
    const hash = loc.hash ?? '';
    return `${pathname}${search}${hash}`;
  };

  const rememberedTarget = useMemo(() => {
    const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (stored && stored.trim().length > 0) {
      return stored;
    }
    return buildRedirectPath(fromLocation);
  }, [fromLocation]);

  useEffect(() => {
    if (isAuthenticated) {
      const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      const destination = stored && stored.trim().length > 0 ? stored : buildRedirectPath(fromLocation);
      if (stored) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      }
      navigate(destination || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, fromLocation]);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, rememberedTarget || '/');
      const { authorizationUrl } = await api.oidcAuthorize();
      window.location.href = authorizationUrl;
    } catch (err) {
      setError((err as Error).message || 'Unable to reach identity provider');
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>IO Marketplace</h1>
        <p className="auth-card__subtitle">
          Sign in with your identity provider to explore, contribute, and approve reusable flows.
        </p>
        {error ? <div className="alert alert-error">{error}</div> : null}
        <button className="button" type="button" onClick={handleLogin} disabled={loading}>
          {loading ? 'Redirecting…' : 'Login with IdP'}
        </button>
        <div className="auth-card__hint">
          <p>
            Your IdP must include a role claim (`admin` or `user`). Users without an approved role
            cannot access the marketplace.
          </p>
        </div>
      </div>
    </div>
  );
}
